import { addDays } from '@/domain/reservations/reservation-time';
import type {
  IGhAction,
  IGhStatsCard,
  IGhStatsTile,
  IGhStatsTileDelta,
  IGhTableCard,
} from '../gpthelp-host-contract';
import { clampText } from './card-text';
import type { ICardFormat } from './card-text';
import type { IStatsPeriod } from './stats-comparison';

/** The sales of a period as `stats_orders` sums them (plan §9.7). */
export interface IStatsSummary {
  /** Net of non-monetary sales (invitaciones, consumo empleado), like the dashboard. */
  sales: number;
  grossSales: number;
  nonMonetary: number;
  orders: number;
  /** Net sales per order. */
  averageTicket: number;
}

export interface IStatsCardInput {
  id: string;
  summary: IStatsSummary;
  /** The period compared with (`comparisonRange`); null: no deltas. */
  previous: IStatsSummary | null;
  footnote: string | null;
  /** Offer «Ver en Estadísticas» (the orders stats page is open to this member). */
  showStatsPage: boolean;
}

export interface ITopProductRow {
  name: string;
  units: number;
  revenue: number;
}

export interface ITopProductsCardInput {
  id: string;
  rows: ITopProductRow[];
  /** «hoy», «ayer», «lun 5/10 – dom 11/10». */
  caption: string;
  footnote: string | null;
  /** Offer «Ver en Estadísticas» (the products stats page is open to this member). */
  showStatsPage: boolean;
}

/** Rows of the «Más vendidos» table. */
export const TOP_PRODUCTS_MAX_ROWS = 10;

/** One branch of the branch comparison table. */
export interface IBranchSalesRow {
  name: string;
  /** null: the member may not see this branch's stats. */
  summary: Pick<IStatsSummary, 'sales' | 'orders' | 'averageTicket'> | null;
}

export interface IBranchSalesCardInput {
  id: string;
  rows: IBranchSalesRow[];
  /** The period, and the channels when filtered. */
  footnote: string | null;
  /** Offer «Ver en Estadísticas» (the orders stats page is open to this member). */
  showStatsPage: boolean;
}

/** Rows of the branch comparison table (plan §8 R2). */
export const BRANCH_SALES_MAX_ROWS = 20;

/** The cells of a branch whose stats the member may not see. */
const NO_ACCESS_CELL = '—';

function trendOf(change: number): IGhStatsTileDelta['trend'] {
  if (change > 0) return 'up';
  return change < 0 ? 'down' : 'flat';
}

function delta(change: number, text: string): IGhStatsTileDelta {
  const trend = trendOf(change);
  return {
    text: clampText(text, 16),
    trend,
    tone: trend === 'up' ? 'success' : trend === 'down' ? 'danger' : 'neutral',
  };
}

/** «▲ 12 %»: the change in whole percents; none when there is nothing to compare with. */
function percentDelta(
  current: number,
  previous: number,
  fmt: ICardFormat
): IGhStatsTileDelta | undefined {
  if (previous <= 0) return undefined;
  const percent = Math.round(((current - previous) / previous) * 100);
  return delta(percent, fmt.percent(Math.abs(percent) / 100));
}

/** «▲ 9»: the change in orders. */
function countDelta(
  current: number,
  previous: number,
  fmt: ICardFormat
): IGhStatsTileDelta {
  const change = current - previous;
  return delta(change, fmt.number(Math.abs(change)));
}

function tile(
  label: string,
  value: string,
  tileDelta: IGhStatsTileDelta | undefined
): IGhStatsTile {
  const result: IGhStatsTile = {
    label: clampText(label, 40),
    value: clampText(value, 24),
  };
  if (tileDelta) result.delta = tileDelta;
  return result;
}

function statsPageAction(
  id: string,
  page: string,
  fmt: ICardFormat
): IGhAction {
  return {
    id,
    kind: 'navigate',
    label: clampText(fmt.t('assistant.stats.viewStats'), 40),
    style: 'outline',
    target: { page },
  };
}

/** Ventas (net), Pedidos and Ticket promedio, with the change against `previous`. */
export function statsCard(
  input: IStatsCardInput,
  fmt: ICardFormat
): IGhStatsCard {
  const { summary, previous } = input;
  const card: IGhStatsCard = {
    id: input.id,
    v: 1,
    type: 'stats',
    title: clampText(fmt.t('assistant.stats.cardTitle'), 80),
    tiles: [
      tile(
        fmt.t('assistant.stats.sales'),
        fmt.money(summary.sales),
        previous ? percentDelta(summary.sales, previous.sales, fmt) : undefined
      ),
      tile(
        fmt.t('assistant.stats.orders'),
        fmt.number(summary.orders),
        previous ? countDelta(summary.orders, previous.orders, fmt) : undefined
      ),
      tile(
        fmt.t('assistant.stats.average'),
        fmt.money(summary.averageTicket),
        previous
          ? percentDelta(summary.averageTicket, previous.averageTicket, fmt)
          : undefined
      ),
    ],
  };
  if (input.footnote) card.footnote = clampText(input.footnote, 160);
  if (input.showStatsPage)
    card.actions = [statsPageAction('stats', 'stats-orders', fmt)];
  return card;
}

/** «Más vendidos»: rank, product, units and total. */
export function topProductsCard(
  input: ITopProductsCardInput,
  fmt: ICardFormat
): IGhTableCard {
  const card: IGhTableCard = {
    id: input.id,
    v: 1,
    type: 'table',
    title: clampText(fmt.t('assistant.stats.topProducts'), 80),
    caption: clampText(input.caption, 40),
    columns: [
      { label: clampText(fmt.t('assistant.stats.product'), 24) },
      { label: clampText(fmt.t('assistant.stats.units'), 24), align: 'end' },
      {
        label: clampText(fmt.t('assistant.stats.total'), 24),
        align: 'end',
        strong: true,
      },
    ],
    rows: input.rows
      .slice(0, TOP_PRODUCTS_MAX_ROWS)
      .map((row) => [
        clampText(row.name, 160),
        clampText(fmt.number(row.units), 160),
        clampText(fmt.money(row.revenue), 160),
      ]),
    rank: true,
  };
  if (input.footnote) card.footnote = clampText(input.footnote, 160);
  if (input.showStatsPage)
    card.actions = [statsPageAction('stats', 'stats-products', fmt)];
  return card;
}

/** Sucursal, Ventas, Pedidos and Ticket promedio of each branch, with its header. */
export function branchSalesCard(
  input: IBranchSalesCardInput,
  fmt: ICardFormat
): IGhTableCard {
  const card: IGhTableCard = {
    id: input.id,
    v: 1,
    type: 'table',
    title: clampText(fmt.t('assistant.stats.cardTitle'), 80),
    columns: [
      { label: clampText(fmt.t('assistant.stats.branch'), 24) },
      {
        label: clampText(fmt.t('assistant.stats.sales'), 24),
        align: 'end',
        strong: true,
      },
      { label: clampText(fmt.t('assistant.stats.orders'), 24), align: 'end' },
      { label: clampText(fmt.t('assistant.stats.average'), 24), align: 'end' },
    ],
    rows: input.rows
      .slice(0, BRANCH_SALES_MAX_ROWS)
      .map(({ name, summary }) => [
        clampText(name, 160),
        summary ? clampText(fmt.money(summary.sales), 160) : NO_ACCESS_CELL,
        summary ? clampText(fmt.number(summary.orders), 160) : NO_ACCESS_CELL,
        summary
          ? clampText(fmt.money(summary.averageTicket), 160)
          : NO_ACCESS_CELL,
      ]),
    // Three figures per row: the header says which is which.
    showHeader: true,
  };
  if (input.footnote) card.footnote = clampText(input.footnote, 160);
  if (input.showStatsPage)
    card.actions = [statsPageAction('stats', 'stats-orders', fmt)];
  return card;
}

/**
 * «hoy», «ayer», the day, or «{from} – {to}»: when the numbers are from,
 * next to the table title.
 */
export function periodCaption(
  period: IStatsPeriod,
  currentDate: string,
  fmt: ICardFormat
): string {
  if (period.first === period.last) {
    if (period.first === currentDate)
      return fmt.t('assistant.stats.caption.today');
    if (period.first === addDays(currentDate, -1))
      return fmt.t('assistant.stats.caption.yesterday');
    return fmt.date(period.first);
  }
  return fmt.t('assistant.stats.caption.range', {
    from: fmt.date(period.first),
    to: fmt.date(period.last),
  });
}

function hour(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * «Día comercial lun 5/10 = lun 5/10 06:00 → mar 6/10 05:59»: shown under
 * the numbers of one whole business day of a branch whose day does not start
 * at midnight. Null otherwise (calendar days need no explanation).
 */
export function businessDayFootnote(
  period: IStatsPeriod,
  startHour: number,
  fmt: ICardFormat
): string | null {
  if (period.partial || period.first !== period.last || startHour <= 0)
    return null;
  const day = fmt.date(period.first);
  return fmt.t('v2Stats.range.businessFootnote', {
    date: day,
    from: `${day} ${hour(startHour)}:00`,
    to: `${fmt.date(addDays(period.first, 1))} ${hour(
      (startHour + 23) % 24
    )}:59`,
  });
}

/** i18n keys of the channels `orderTypes` filters by (the day close's names). */
const CHANNEL_KEYS = new Map<string, string>([
  ['seat', 'v2Stats.dayClose.channelSeat'],
  ['away', 'v2Stats.dayClose.channelAway'],
  ['delivery', 'v2Stats.dayClose.channelDelivery'],
]);

/**
 * «Para llevar»: the channels the numbers are filtered to, so a filtered
 * card never reads as the store's total sales. Null without a filter.
 */
export function channelsLabel(
  orderTypes: string[] | null,
  fmt: ICardFormat
): string | null {
  const names = [...new Set(orderTypes ?? [])]
    .map((type) => CHANNEL_KEYS.get(type))
    .filter((key): key is string => !!key)
    .map((key) => fmt.t(key));
  return names.length ? fmt.list(names) : null;
}

/** The footnote parts that apply, joined with « · »; null when none does. */
export function joinFootnotes(parts: Array<string | null>): string | null {
  const present = parts.filter((part): part is string => !!part);
  return present.length ? present.join(' · ') : null;
}
